import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    NumberField,
    useTranslate,
    FunctionField,
    useRecordContext,
    useRedirect,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Typography,
    Paper,
    Divider,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    ArrowBack,
    Timeline,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Speed,
    Bolt,
    Memory,
    PowerSettingsNew,
    TrendingUp,
    Assessment,
    Info,
} from '@mui/icons-material';

// Get trigger reason info
const getTriggerInfo = (reason: string) => {
    switch (reason) {
        case 'SESSION_START':
            return { color: 'success', icon: <CheckCircle /> };
        case 'SESSION_STOP':
            return { color: 'info', icon: <CheckCircle /> };
        case 'STATUS_CHANGE':
            return { color: 'warning', icon: <Warning /> };
        case 'PERIODIC':
            return { color: 'default', icon: <Timeline /> };
        case 'MANUAL':
            return { color: 'primary', icon: <Speed /> };
        default:
            return { color: 'default', icon: <Timeline /> };
    }
};

// Get allocation strategy color
const getStrategyColor = (strategy: string) => {
    switch (strategy) {
        case 'equal':
        case 'equal_distribution':
            return '#2196F3';
        case 'queue':
            return '#FF9800';
        case 'member_priority':
            return '#9C27B0';
        default:
            return '#757575';
    }
};

// Page header component
const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();

    if (!record) return null;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 4 },
            gap: { xs: 1.5, sm: 2 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    onClick={() => redirect('/lms-logs')}
                    size="large"
                    sx={{
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box>
                    <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 600, mb: 0.5 }}>
                        {translate('resources.lms-logs.show.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        <DateField
                            source="decisionTimestamp"
                            showTime
                            options={{
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            }}
                        />
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FunctionField
                    render={(record: any) => {
                        const { color, icon } = getTriggerInfo(record.triggerReason);
                        return (
                            <Chip
                                label={translate(`resources.lms-logs.triggers.${record.triggerReason}`)}
                                color={color as any}
                                icon={icon}
                                sx={{ fontWeight: 500 }}
                            />
                        );
                    }}
                />
            </Box>
        </Box>
    );
};

// Basic Information Section
const BasicInformation = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    const strategyMap: { [key: string]: string } = {
        'equal': 'Equal Distribution',
        'equal_distribution': 'Equal Distribution',
        'queue': 'Queue (FCFS)',
        'member_priority': 'Member Priority'
    };

    // Extract from nested structure
    const strategy = record.output?.allocationStrategy || record.allocationStrategy;
    const executionTime = record.results?.executionTimeMs || record.executionTimeMs;
    const error = record.results?.error || record.error;
    const loadGroup = record.loadGroup || {};
    const ocppServer = record.ocppServer || {};

    return (
        <Card sx={{ overflow: 'hidden', mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Info sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {translate('resources.lms-logs.show.basicInfo')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {translate('resources.lms-logs.fields.loadGroup')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {loadGroup.name || record.loadGroupName || record.loadGroupId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {loadGroup.id || record.loadGroupId}
                        </Typography>
                        {loadGroup.maxCurrent && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                Max: {loadGroup.maxCurrent}A
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {translate('resources.lms-logs.fields.ocppServer')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ocppServer.name || record.ocppServerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {ocppServer.id || record.ocppServerId}
                        </Typography>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {translate('resources.lms-logs.fields.allocationStrategy')}
                        </Typography>
                        <Chip
                            label={strategyMap[strategy] || strategy}
                            icon={<Memory />}
                            sx={{
                                fontWeight: 500,
                                backgroundColor: getStrategyColor(strategy) + '20',
                                color: getStrategyColor(strategy),
                            }}
                        />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {translate('resources.lms-logs.fields.executionTime')}
                        </Typography>
                        {error ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ErrorIcon sx={{ fontSize: 18, color: '#FF5252' }} />
                                <Typography variant="body2" color="error">
                                    {translate('resources.lms-logs.status.error')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircle sx={{ fontSize: 18, color: '#00C853' }} />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {executionTime}ms
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// Power Allocation Statistics
const PowerAllocationStats = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    // Extract from nested structure
    const activeChargers = record.input?.activeChargers || record.activeChargers || 0;
    const pendingChargers = record.input?.pendingChargers || record.pendingChargers || 0;
    const totalRequested = record.input?.totalRequestedCurrent || record.totalRequestedCurrent || 0;
    const totalAllocated = record.output?.totalAllocatedCurrent || record.totalAllocatedCurrent || 0;
    const available = record.input?.availableCurrent || record.availableCurrent || 0;
    const loadGroup = record.loadGroup || {};
    const maxCurrent = loadGroup.maxCurrent || record.maxCurrent || 40; // Default 40A if not available

    // Calculate utilization based on allocated vs max current (the actual limit)
    const utilization = maxCurrent > 0 ? (totalAllocated / maxCurrent * 100) : 0;

    // Calculate oversubscription (how much is requested vs what's available)
    const oversubscription = maxCurrent > 0 ? (totalRequested / maxCurrent * 100) : 0;

    // Profile stats from results
    const profilesSent = record.results?.profilesSent || record.profilesSent || 0;
    const profilesAccepted = record.results?.profilesAccepted || record.profilesAccepted || 0;
    const profilesRejected = record.results?.profilesRejected || record.profilesRejected || 0;

    return (
        <Card sx={{ overflow: 'hidden', mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PowerSettingsNew sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {translate('resources.lms-logs.show.powerAllocation')}
                    </Typography>
                </Box>

                {/* Power Flow Visualization */}
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, backgroundColor: '#F8F9FA' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 auto' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Load Group Limit
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 600 }}>
                                {maxCurrent}A
                            </Typography>
                        </Box>

                        <Typography sx={{ color: '#9E9E9E', display: { xs: 'none', sm: 'block' } }}>→</Typography>

                        <Box sx={{ flex: '1 1 auto' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Available (80% safety)
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 600, color: '#1976D2' }}>
                                {available.toFixed(1)}A
                            </Typography>
                        </Box>

                        <Typography sx={{ color: '#9E9E9E', display: { xs: 'none', sm: 'block' } }}>→</Typography>

                        <Box sx={{ flex: '1 1 auto' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Sum of Chargers' Max
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                    fontWeight: 600,
                                    color: totalRequested > available ? '#FF9800' : '#00C853'
                                }}
                            >
                                {totalRequested.toFixed(1)}A
                            </Typography>
                            {activeChargers > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {activeChargers} × {(totalRequested / activeChargers).toFixed(1)}A each
                                </Typography>
                            )}
                            {totalRequested > available && (
                                <Typography variant="caption" color="warning.main" display="block">
                                    {oversubscription.toFixed(0)}% of limit
                                </Typography>
                            )}
                        </Box>

                        <Typography sx={{ color: '#9E9E9E', display: { xs: 'none', sm: 'block' } }}>→</Typography>

                        <Box sx={{ flex: '1 1 auto' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Actually Allocated
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 600, color: '#00C853' }}>
                                {totalAllocated.toFixed(1)}A
                            </Typography>
                            {totalRequested > totalAllocated && (
                                <Typography variant="caption" color="info.main">
                                    Power sharing active
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Paper>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', backgroundColor: '#E8F5E8' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Bolt sx={{ color: '#00C853' }} />
                                <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600, color: '#00C853' }}>
                                    {activeChargers}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {translate('resources.lms-logs.show.activeChargers')}
                            </Typography>
                            {pendingChargers > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {`+ ${pendingChargers} pending`}
                                </Typography>
                            )}
                        </Paper>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center', backgroundColor: '#E3F2FD' }}>
                            <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600, color: '#1976D2' }}>
                                {utilization.toFixed(0)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Load Group Utilization
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {totalAllocated.toFixed(1)}A / {maxCurrent}A
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(utilization, 100)}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#E0E0E0',
                                    mt: 1,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: utilization > 90 ? '#FF5252' : utilization > 70 ? '#FF9800' : '#00C853',
                                        borderRadius: 4,
                                    },
                                }}
                            />
                        </Paper>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                        <Paper sx={{
                            p: { xs: 1.5, sm: 2 },
                            textAlign: 'center',
                            backgroundColor: totalRequested > available ? '#FFF3E0' : '#E8F5E9'
                        }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.5rem', sm: '2rem' },
                                    fontWeight: 600,
                                    color: totalRequested > available ? '#EF6C00' : '#00C853'
                                }}
                            >
                                {totalRequested > available ? 'Sharing' : 'Normal'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Power Mode
                            </Typography>
                            {totalRequested > available ? (
                                <Typography variant="caption" color="warning.main">
                                    Demand exceeds capacity
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="success.main">
                                    Within capacity
                                </Typography>
                            )}
                        </Paper>
                    </Box>
                </Box>

                {/* Charging Profiles */}
                {(profilesSent > 0 || profilesAccepted > 0 || profilesRejected > 0) && (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            {translate('resources.lms-logs.show.profileStats')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={`${profilesSent} Sent`}
                                size="small"
                                sx={{ backgroundColor: '#E3F2FD' }}
                            />
                            <Chip
                                label={`${profilesAccepted} Accepted`}
                                size="small"
                                color="success"
                            />
                            {profilesRejected > 0 && (
                                <Chip
                                    label={`${profilesRejected} Rejected`}
                                    size="small"
                                    color="error"
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

// Allocations Table
const AllocationsTable = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    // Extract from nested structure
    const allocations = record.output?.allocations || record.allocations || [];

    if (!allocations || allocations.length === 0) {
        return null;
    }

    return (
        <Card sx={{ overflow: 'hidden', mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Assessment sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {translate('resources.lms-logs.show.allocations')}
                    </Typography>
                </Box>

                <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('resources.lms-logs.show.chargerStation')}</TableCell>
                                <TableCell align="right">{translate('resources.lms-logs.show.currentLimit')}</TableCell>
                                <TableCell align="right">Power Granted</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allocations.map((allocation: any, index: number) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {allocation.chargerName || allocation.stationId}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {allocation.chargerId}
                                            </Typography>
                                            {allocation.stationId && allocation.chargerName && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Station: {allocation.stationId}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`${allocation.currentLimit}A`}
                                            size="small"
                                            sx={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: allocation.reductionFactor < 1 ? '#FF9800' : '#00C853',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {(allocation.reductionFactor * 100).toFixed(0)}%
                                            </Typography>
                                            {allocation.reductionFactor < 1 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    of max
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>
            </CardContent>
        </Card>
    );
};

// Trigger Details
const TriggerDetails = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record?.triggerDetails || Object.keys(record.triggerDetails).length === 0) {
        return null;
    }

    const details = record.triggerDetails;

    return (
        <Card sx={{ overflow: 'hidden', mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Timeline sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {translate('resources.lms-logs.show.triggerDetails')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                    {details.chargerId && (
                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: { xs: '1 1 100%', sm: '1 1 300px' }, backgroundColor: '#F8F9FA' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Bolt sx={{ fontSize: 20, color: '#1976D2', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Triggering Charger
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {details.chargerName || details.chargerId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                ID: {details.chargerId}
                            </Typography>
                            {details.transactionId && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Transaction: {details.transactionId}
                                </Typography>
                            )}
                        </Paper>
                    )}

                    {details.eventSummary && (
                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: { xs: '1 1 100%', sm: '1 1 300px' }, backgroundColor: '#FFF8E1' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Info sx={{ fontSize: 20, color: '#FF9800', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Event Summary
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                {details.eventSummary}
                            </Typography>
                        </Paper>
                    )}

                    {details.consolidationWindow && (
                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: { xs: '1 1 100%', sm: '1 1 200px' }, backgroundColor: '#E8F5E9' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Speed sx={{ fontSize: 20, color: '#00C853', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Consolidation
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {details.consolidationWindow.duration}ms window
                            </Typography>
                            {details.eventCount && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {details.eventCount} events consolidated
                                </Typography>
                            )}
                        </Paper>
                    )}
                </Box>

                {details.stoppedSessions && details.stoppedSessions.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                            Stopped Sessions ({details.stoppedSessions.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}>
                            {details.stoppedSessions.map((session: any, index: number) => (
                                <Paper
                                    key={index}
                                    sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        flex: { xs: '1 1 100%', sm: '1 1 280px' },
                                        backgroundColor: '#F5F5F5',
                                        border: '1px solid #E0E0E0'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <PowerSettingsNew sx={{ fontSize: 18, color: '#757575', mr: 1 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {session.chargerName || session.chargerId}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        ID: {session.chargerId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Transaction: {session.transactionId}
                                    </Typography>
                                    {session.statusProgression && (
                                        <Chip
                                            label={session.statusProgression}
                                            size="small"
                                            sx={{ mt: 1 }}
                                            variant="outlined"
                                        />
                                    )}
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                )}

                {details.statusChanges && details.statusChanges.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                            Status Changes
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {details.statusChanges.map((change: any, index: number) => (
                                <Paper
                                    key={index}
                                    sx={{
                                        p: 1.5,
                                        backgroundColor: '#E3F2FD',
                                        border: '1px solid #90CAF9'
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {change.chargerId}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <Chip
                                            label={change.oldStatus}
                                            size="small"
                                            sx={{ backgroundColor: '#FFF' }}
                                        />
                                        <Typography variant="caption" color="text.secondary">→</Typography>
                                        <Chip
                                            label={change.newStatus}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                        />
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                )}

                {(details.affectedChargers || details.affectedChargersWithNames) &&
                 (details.affectedChargers?.length > 0 || details.affectedChargersWithNames?.length > 0) && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                            Affected Chargers ({details.affectedChargersWithNames?.length || details.affectedChargers?.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {details.affectedChargersWithNames ? (
                                details.affectedChargersWithNames.map((charger: any, index: number) => (
                                    <Paper
                                        key={index}
                                        sx={{
                                            p: 1.5,
                                            backgroundColor: '#E8EAF6',
                                            border: '1px solid #D1C4E9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <Bolt sx={{ fontSize: 18, color: '#5E35B1' }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {charger.name || charger.id}
                                            </Typography>
                                            {charger.name && (
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {charger.id}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                ))
                            ) : (
                                details.affectedChargers.map((chargerId: string, index: number) => (
                                    <Chip
                                        key={index}
                                        icon={<Bolt />}
                                        label={chargerId}
                                        sx={{
                                            backgroundColor: '#E8EAF6',
                                            '& .MuiChip-icon': {
                                                fontSize: 16,
                                                color: '#5E35B1'
                                            }
                                        }}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                )}

                {details.event && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Event Information
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                                label={details.event}
                                color="primary"
                                variant="outlined"
                            />
                            {details.stationId && (
                                <Typography variant="body2" color="text.secondary">
                                    Station: {details.stationId}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

// Error Display
const ErrorDisplay = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    // Extract from nested structure
    const error = record.results?.error || record.error;

    if (!error) return null;

    return (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" gutterBottom>
                {translate('resources.lms-logs.show.errorDetails')}
            </Typography>
            <Typography variant="body2">
                {error}
            </Typography>
        </Alert>
    );
};

export const LmsLogShow = () => {
    const translate = useTranslate();

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
                    <PageHeader />

                    <BasicInformation />

                    <PowerAllocationStats />

                    <AllocationsTable />

                    <TriggerDetails />

                    <ErrorDisplay />
                </Box>
            </SimpleShowLayout>
        </Show>
    );
};